import { useQuery } from '@tanstack/react-query';

import { getJobTitles } from '@/services/jobTitleService';
import type { JobTitleDto } from '@/types/jobTitle';

const CASCADE_PAGE_SIZE = 500;

interface UseJobTitlesForCascadeResult {
  jobTitles: JobTitleDto[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  hasMore: boolean;
}

/**
 * Fetches active job titles scoped to a given department (used by the
 * EmployeeForm cascade). Disabled until `departmentId` is set; switching the
 * department remounts the query under a new key so stale options never bleed
 * across selections.
 */
export function useJobTitlesForCascade(
  departmentId: string | undefined,
): UseJobTitlesForCascadeResult {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['job-titles-cascade', departmentId],
    queryFn: () =>
      getJobTitles(
        { page: 0, size: CASCADE_PAGE_SIZE },
        { filters: { status: 'ACTIVE', departmentIds: [departmentId!] } },
      ),
    enabled: !!departmentId,
  });

  return {
    jobTitles: data?.content ?? [],
    isLoading,
    isError,
    error,
    hasMore: (data?.totalElements ?? 0) > CASCADE_PAGE_SIZE,
  };
}
